import { Slot } from '@radix-ui/react-slot'
import { canRenderInJsx } from '@repo/internal-tools-react-utils/src/lib/canRenderInJsx.ts'
import { type ComponentPropsWithRef } from 'react'
import { cn } from '../../utils/cn.ts'
import type { WithDataTestId } from '../componentProps.types.ts'
import { IconContainer } from '../IconContainer/IconContainer.tsx'
import { IconContainerOptionsProvider } from '../IconContainer/IconContainerOptionsProvider.tsx'
import {
  getContentDescriptionContainerLayoutStyle,
  type GetContentDescriptionContainerLayoutStyleProps,
  getContentDescriptionIconSizeByContentSize,
  getContentDescriptionLayoutStyle,
  type GetContentDescriptionLayoutStyleProps,
  getContentDescriptionStateStyle,
  type GetContentDescriptionStateStyleProps,
  getContentElementContainerLayoutStyle,
  type GetContentElementContainerLayoutStyleProps,
  getContentLabelIconSizeByContentSize,
  getContentLabelLayoutStyle,
  type GetContentLabelLayoutStyleProps,
  getContentRootLayoutStyle,
  type GetContentRootLayoutStyleProps,
} from './Content.styles.ts'
import type { ContentProps } from './Content.types.ts'
import { useContentOptionsContext } from './useContentOptionsContext.ts'

export interface ContentLabelProps
  extends ComponentPropsWithRef<'div'>,
    GetContentLabelLayoutStyleProps,
    WithDataTestId {
  asChild?: boolean
}

/**
 * The label of the content.
 */
export const ContentLabel = (props: ContentLabelProps) => {
  const {
    ref,
    asChild,
    font,
    size,
    align,
    shouldWrapLabel,
    className,
    ...restProps
  } = props

  const contextOptions = useContentOptionsContext()
  const mergedFont = font ?? contextOptions.font
  const mergedSize = size ?? contextOptions.size
  const mergedAlign = align ?? contextOptions.labelAlign ?? contextOptions.align
  const mergedShouldWrap = shouldWrapLabel ?? contextOptions.shouldWrapLabel

  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      ref={ref}
      className={cn(
        getContentLabelLayoutStyle({
          font: mergedFont,
          size: mergedSize,
          align: mergedAlign,
          shouldWrapLabel: mergedShouldWrap,
        }),
        contextOptions.labelClassName,
        className,
      )}
      data-testid="content-label"
      {...restProps}
    />
  )
}

export interface ContentLabelContainerProps
  extends ComponentPropsWithRef<'div'>,
    Pick<GetContentLabelLayoutStyleProps, 'size'>,
    GetContentElementContainerLayoutStyleProps,
    WithDataTestId {
  asChild?: boolean
}

/**
 * The container that wraps label and its icon.
 * It controls the layout of how label and the icon are displayed.
 *
 * If there's no icon, this component is not needed.
 */
export const ContentLabelContainer = (props: ContentLabelContainerProps) => {
  const { ref, asChild, size, align, className, ...restProps } = props

  const contextOptions = useContentOptionsContext()
  const mergedSize = size ?? contextOptions.size
  const mergedAlign = align ?? contextOptions.labelAlign ?? contextOptions.align

  const Comp = asChild ? Slot : 'div'

  return (
    <IconContainerOptionsProvider
      context="contained"
      size={getContentLabelIconSizeByContentSize(mergedSize)}
    >
      <Comp
        ref={ref}
        className={cn(
          getContentElementContainerLayoutStyle({ align: mergedAlign }),
          contextOptions.cellClassName,
          contextOptions.labelCellClassName,
          className,
        )}
        {...restProps}
      />
    </IconContainerOptionsProvider>
  )
}

export interface ContentDescriptionProps
  extends ComponentPropsWithRef<'div'>,
    GetContentDescriptionLayoutStyleProps,
    GetContentDescriptionStateStyleProps,
    WithDataTestId {
  asChild?: boolean
}

/**
 * The description of the content.
 */
export const ContentDescription = (props: ContentDescriptionProps) => {
  const {
    ref,
    asChild,
    font,
    size,
    align,
    shouldWrapDescription,
    hasIssue,
    className,
    ...restProps
  } = props

  const contextOptions = useContentOptionsContext()
  const mergedFont = font ?? contextOptions.font
  const mergedSize = size ?? contextOptions.size
  const mergedAlign =
    align ?? contextOptions.descriptionAlign ?? contextOptions.align
  const mergedShouldWrap =
    shouldWrapDescription ?? contextOptions.shouldWrapDescription

  const Comp = asChild ? Slot : 'div'

  return (
    <Comp
      ref={ref}
      className={cn(
        getContentDescriptionLayoutStyle({
          font: mergedFont,
          size: mergedSize,
          align: mergedAlign,
          shouldWrapDescription: mergedShouldWrap,
        }),
        getContentDescriptionStateStyle({
          hasIssue,
        }),
        contextOptions.descriptionClassName,
        className,
      )}
      data-testid="content-description"
      {...restProps}
    />
  )
}

export interface ContentDescriptionContainerProps
  extends ComponentPropsWithRef<'div'>,
    GetContentElementContainerLayoutStyleProps,
    GetContentDescriptionContainerLayoutStyleProps,
    Pick<GetContentLabelLayoutStyleProps, 'size'>,
    WithDataTestId {
  asChild?: boolean
}

/**
 * The container that wraps the description and its icon.
 * It controls the layout of how the description and the icon are displayed.
 *
 * If there's no icon, this component is not needed.
 */
export const ContentDescriptionContainer = (
  props: ContentDescriptionContainerProps,
) => {
  const { ref, asChild, align, direction, size, className, ...restProps } =
    props

  const contextOptions = useContentOptionsContext()
  const mergedSize = size ?? contextOptions.size
  const mergedAlign =
    align ?? contextOptions.descriptionAlign ?? contextOptions.align
  const mergedDirection = direction ?? contextOptions.direction

  const Comp = asChild ? Slot : 'div'

  return (
    <IconContainerOptionsProvider
      context="contained"
      size={getContentDescriptionIconSizeByContentSize(mergedSize)}
    >
      <Comp
        ref={ref}
        className={cn(
          getContentElementContainerLayoutStyle({ align: mergedAlign }),
          getContentDescriptionContainerLayoutStyle({
            direction: mergedDirection,
          }),
          contextOptions.cellClassName,
          contextOptions.descriptionCellClassName,
          className,
        )}
        {...restProps}
      />
    </IconContainerOptionsProvider>
  )
}

export interface ContentRootProps
  extends ComponentPropsWithRef<'div'>,
    GetContentRootLayoutStyleProps,
    WithDataTestId {}

export const ContentRoot = (props: ContentRootProps) => {
  const { ref, className, size, direction, ...restProps } = props

  const contextOptions = useContentOptionsContext()
  const mergedSize = size ?? contextOptions.size
  const mergedDirection = direction ?? contextOptions?.direction

  return (
    <div
      ref={ref}
      className={cn(
        getContentRootLayoutStyle({
          size: mergedSize,
          direction: mergedDirection,
        }),
        contextOptions.className,
        className,
      )}
      {...restProps}
    />
  )
}

export const Content = (props: ContentProps) => {
  const {
    className,
    cellClassName,
    labelCellClassName,
    label,
    labelClassName,
    labelIcon,
    labelAlign,
    descriptionCellClassName,
    description,
    descriptionClassName,
    descriptionIcon,
    descriptionAlign,
    font,
    size,
    direction,
    align,
    shouldWrapLabel,
    shouldWrapDescription,
    hasIssue,
    'data-testid': dataTestId = 'content',
  } = props

  const mergedLabelAlign = labelAlign ?? align
  const mergedDescriptionAlign = descriptionAlign ?? align

  return (
    <ContentRoot
      size={size}
      direction={direction}
      className={className}
      data-testid={dataTestId}
    >
      {(canRenderInJsx(label) || canRenderInJsx(labelIcon)) && (
        <ContentLabelContainer
          size={size}
          align={mergedLabelAlign}
          className={cn(cellClassName, labelCellClassName)}
        >
          {canRenderInJsx(label) && (
            <ContentLabel
              font={font}
              size={size}
              align={mergedLabelAlign}
              shouldWrapLabel={shouldWrapLabel}
              className={labelClassName}
            >
              {label}
            </ContentLabel>
          )}
          {canRenderInJsx(labelIcon) && (
            <IconContainer>{labelIcon}</IconContainer>
          )}
        </ContentLabelContainer>
      )}
      {(canRenderInJsx(description) || canRenderInJsx(descriptionIcon)) && (
        <ContentDescriptionContainer
          size={size}
          className={cn(cellClassName, descriptionCellClassName)}
          direction={direction}
          align={mergedDescriptionAlign}
        >
          {canRenderInJsx(description) && (
            <ContentDescription
              font={font}
              size={size}
              align={mergedDescriptionAlign}
              shouldWrapDescription={shouldWrapDescription}
              hasIssue={hasIssue}
              className={descriptionClassName}
            >
              {description}
            </ContentDescription>
          )}
          {canRenderInJsx(descriptionIcon) && (
            <IconContainer>{descriptionIcon}</IconContainer>
          )}
        </ContentDescriptionContainer>
      )}
    </ContentRoot>
  )
}
